import { router, usePage } from "@inertiajs/react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn, populateQueryParams } from "@/lib/utils";

export interface RecordStepperProps {
	/** Ids of every row the table is currently showing, in display order. */
	ids: string[];
	/** Id of the record the panel is showing right now. */
	currentId: string;
	/** Query param that carries the open record, e.g. "view_appointment". */
	param: string;
	/** Deferred props to re-request on a swap, on top of the shared three. */
	only: string[];
	prevLabel?: string;
	nextLabel?: string;
	className?: string;
}

/**
 * Steps a detail dialog through the rows of the page the table is showing,
 * without closing it. One segmented control rather than a counter plus a
 * toolbar: full width on touch so the two targets sit at opposite edges — the
 * furthest apart they can be, so a thumb cannot mis-tap the wrong direction —
 * and a quiet inline pill from lg.
 *
 * Renders nothing when the open record is not on the current page, which is
 * what happens when the panel was reached some other way (a series sibling, a
 * deep link). There is no meaningful "next" in that case.
 */
const RecordStepper = memo(function Component({
	ids,
	currentId,
	param,
	only,
	prevLabel,
	nextLabel,
	className,
}: RecordStepperProps) {
	const { t } = useTranslation();
	const { url: pageURL } = usePage();
	// The swap is a network hop, so the control locks while it is in flight —
	// otherwise a second tap on a slow connection skips a record. It also records
	// which arrow started it, so only that one spins.
	const [pending, setPending] = useState<"prev" | "next" | null>(null);

	const siblings = useMemo(() => {
		const index = ids.indexOf(currentId);

		if (index === -1) return null;

		return {
			position: index + 1,
			total: ids.length,
			prev: ids[index - 1] ?? null,
			next: ids[index + 1] ?? null,
		};
	}, [ids, currentId]);

	if (!siblings) return null;

	const go = (id: string, from: "prev" | "next") => {
		const { fullUrl } = populateQueryParams(pageURL, { [param]: id });

		router.get(
			fullUrl,
			{},
			{
				only: ["adminPortal", "flash", "errors", ...only],
				preserveScroll: true,
				preserveState: true,
				onStart: () => setPending(from),
				onFinish: () => setPending(null),
			},
		);
	};

	return (
		<div className={cn("flex", className)}>
			<div className="flex items-center justify-between w-full overflow-hidden border rounded-lg divide-x border-border divide-border bg-card lg:w-auto lg:justify-start">
				<Button
					variant="ghost"
					size="icon"
					className="rounded-none shadow-none size-11 shrink-0 lg:size-8"
					disabled={!siblings.prev || !!pending}
					aria-label={prevLabel ?? t("components.record_stepper.prev")}
					onClick={() => {
						if (siblings.prev) go(siblings.prev, "prev");
					}}
				>
					{pending === "prev" ? (
						<Loader2 className="animate-spin" />
					) : (
						<ChevronLeft />
					)}
				</Button>

				<p
					className="px-3 text-[11px] font-light tabular-nums truncate text-muted-foreground lg:text-xs"
					aria-live="polite"
				>
					{t("components.record_stepper.position", {
						position: siblings.position,
						total: siblings.total,
					})}
				</p>

				<Button
					variant="ghost"
					size="icon"
					className="rounded-none shadow-none size-11 shrink-0 lg:size-8"
					disabled={!siblings.next || !!pending}
					aria-label={nextLabel ?? t("components.record_stepper.next")}
					onClick={() => {
						if (siblings.next) go(siblings.next, "next");
					}}
				>
					{pending === "next" ? (
						<Loader2 className="animate-spin" />
					) : (
						<ChevronRight />
					)}
				</Button>
			</div>
		</div>
	);
});

export default RecordStepper;
